import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  CustomField,
  CustomFieldEntity,
  CustomFieldPayload,
  CustomFieldValues,
} from "../features/customFields/customFields.types";

export const customFieldsSlice = createApi({
  reducerPath: "customFieldsSlice",
  tagTypes: ["CustomField", "CustomFieldValues"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getCustomFields: build.query<CustomField[], { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/custom-fields/fields",
        params: { orgId },
      }),
      providesTags: ["CustomField"],
    }),
    canManageCustomFields: build.query<boolean, { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/custom-fields/can-manage",
        params: { orgId },
      }),
    }),
    createCustomField: build.mutation<CustomField, CustomFieldPayload>({
      query: (body) => ({
        url: "api/custom-fields/fields",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CustomField"],
    }),
    updateCustomField: build.mutation<CustomField, CustomFieldPayload>({
      query: (body) => ({
        url: "api/custom-fields/fields",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CustomField"],
    }),
    deleteCustomField: build.mutation<boolean, { id: string }>({
      query: ({ id }) => ({
        url: `api/custom-fields/fields/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomField"],
    }),
    reorderCustomFields: build.mutation<
      CustomField[],
      { orgId: string; entityType: CustomFieldEntity; fieldIds: string[] }
    >({
      query: (body) => ({
        url: "api/custom-fields/fields/order",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CustomField"],
    }),
    getCustomFieldValues: build.query<
      CustomFieldValues,
      { customerId: string }
    >({
      query: ({ customerId }) => ({
        url: "api/custom-fields/values",
        params: { customerId },
      }),
      providesTags: ["CustomFieldValues"],
    }),
    saveCustomFieldValues: build.mutation<
      CustomFieldValues,
      { customerId: string; values: Record<string, string> }
    >({
      query: (body) => ({
        url: "api/custom-fields/values",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CustomFieldValues"],
    }),
  }),
});

export const {
  useGetCustomFieldsQuery,
  useCanManageCustomFieldsQuery,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldMutation,
  useReorderCustomFieldsMutation,
  useGetCustomFieldValuesQuery,
  useSaveCustomFieldValuesMutation,
} = customFieldsSlice;
